import { useMemo, useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { useLocation } from "wouter";
import { useSearch } from "wouter/use-browser-location";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Skeleton } from "@/components/ui/skeleton";
import { Checkbox } from "@/components/ui/checkbox";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "@/components/ui/dialog";
import { Search, User, ArrowLeft, ChevronRight, UserPlus, Loader2, Waves } from "lucide-react";
import type { AccessSources } from "../../../shared/admin-agent";
import type { PlanTier } from "../../../shared/agent-plan";
import { isPaddleReference, parseUserListState, userDetailHref, userListHref } from "@/lib/admin-user-navigation";

interface User {
  id: string;
  email: string | null;
  firstName: string | null;
  lastName: string | null;
  profileImageUrl: string | null;
  isSuspended: boolean;
  createdAt: string;
  updatedAt: string;
  effectiveTier: PlanTier;
  accessSources: AccessSources;
}

type UserSourceFilter = "all" | keyof AccessSources;
type AdminUsersPage = { users: User[]; total: number; limit: number; offset: number; lookup?: { status: "matched" | "missing" | "ambiguous" | "unavailable"; historical: boolean; message: string } };

interface UserStats {
  totalUsers: number;
  newUsersThisMonth: number;
  activeUsers: number;
  topLocations: Array<{ name: string; favoriteCount: number }>;
}

interface CreateUserForm {
  firstName: string;
  lastName: string;
  email: string;
  phoneNumber: string;
  password: string;
  confirmPassword: string;
  grantPro: boolean;
  grantProReason: string;
  grantProAcknowledged: boolean;
}

const emptyForm = (): CreateUserForm => ({
  firstName: "",
  lastName: "",
  email: "",
  phoneNumber: "",
  password: "",
  confirmPassword: "",
  grantPro: false,
  grantProReason: "",
  grantProAcknowledged: false,
});

function sourceName(source: keyof AccessSources) {
  return ({
    paidAgent: "Paid Agent",
    paidPro: "Paid Pro",
    complimentaryAgent: "Comp Agent",
    complimentaryPro: "Comp Pro",
    test: "Test",
  } satisfies Record<keyof AccessSources, string>)[source];
}

interface UserDatabaseProps {
  onClose: () => void;
}

export default function UserDatabase({ onClose }: UserDatabaseProps) {
  const [, navigate] = useLocation();
  const locationSearch = useSearch();
  const listState = useMemo(() => parseUserListState(locationSearch), [locationSearch]);
  const { search: searchTerm, tier, source, offset, environment } = listState;
  const changeListState = (changes: Partial<typeof listState>, replace = false) =>
    navigate(userListHref({ ...listState, ...changes }), replace ? { replace: true } : undefined);
  const queryClient = useQueryClient();

  const [showCreateDialog, setShowCreateDialog] = useState(false);
  const [form, setForm] = useState<CreateUserForm>(emptyForm());
  const [formError, setFormError] = useState<string | null>(null);

  // ── Queries ─────────────────────────────────────────────────────────────
  const { data: userStats, isLoading: statsLoading } = useQuery<UserStats>({
    queryKey: ["/api/admin/user-stats"],
    refetchInterval: 60000,
  });

  const { data: usersPage, isLoading: usersLoading, error: usersError, isError: usersIsError, refetch: refetchUsers } = useQuery<AdminUsersPage, Error>({
    queryKey: ["/api/admin/users", searchTerm, tier, source, offset, environment],
    enabled: !isPaddleReference(searchTerm) || !!environment,
    queryFn: async () => {
      const params = new URLSearchParams({ limit: "25", offset: String(offset) });
      if (searchTerm.trim()) params.set("search", searchTerm.trim());
      if (isPaddleReference(searchTerm) && environment) params.set("environment", environment);
      if (tier !== "all") params.set("tier", tier);
      if (source !== "all") params.set("source", source);
      const res = await fetch(`/api/admin/users?${params}`, { credentials: "include", headers: { Accept: "application/json" } });
      if (!res.ok) {
        const body = await res.json().catch(() => ({}));
        throw new Error(body.message ?? `Could not load members (HTTP ${res.status}).`);
      }
      return res.json();
    },
    refetchInterval: 30000,
  });

  // ── Create user mutation ─────────────────────────────────────────────────
  const createUserMutation = useMutation({
    mutationFn: async (data: Omit<CreateUserForm, "confirmPassword" | "grantProReason" | "grantProAcknowledged"> & { reason?: string }) => {
      const res = await fetch("/api/admin/users", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify({
          email: data.email.trim().toLowerCase(),
          phoneNumber: data.phoneNumber.trim(),
          password: data.password,
          firstName: data.firstName.trim() || null,
          lastName: data.lastName.trim() || null,
          grantPro: data.grantPro,
          ...(data.grantPro ? { confirm: true, reason: data.reason } : {}),
        }),
      });
      if (!res.ok) {
        const body = await res.json().catch(() => ({}));
        throw new Error(body.message ?? "Failed to create user");
      }
      return res.json() as Promise<User>;
    },
    onSuccess: (newUser) => {
      queryClient.invalidateQueries({ queryKey: ["/api/admin/users"] });
      queryClient.invalidateQueries({ queryKey: ["/api/admin/user-stats"] });
      queryClient.invalidateQueries({ queryKey: ["/api/admin/agent-overview"] });
      setShowCreateDialog(false);
      setForm(emptyForm());
      setFormError(null);
      // Navigate directly to the new user's detail page
      navigate(userDetailHref(newUser.id, listState));
    },
    onError: (err: Error) => {
      setFormError(err.message);
    },
  });

  const handleCreateSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    setFormError(null);

    if (!form.email.trim()) {
      setFormError("Email address is required.");
      return;
    }
    if (!/^\+[1-9]\d{7,14}$/.test(form.phoneNumber.trim())) {
      setFormError("Enter a phone number in international format, for example +14155552671.");
      return;
    }
    if (form.password.length < 8) {
      setFormError("Password must be at least 8 characters.");
      return;
    }
    if (form.password !== form.confirmPassword) {
      setFormError("Passwords do not match.");
      return;
    }
    if (form.grantPro && (!form.grantProReason.trim() || form.grantProReason.trim().length > 500)) {
      setFormError("A reason between 1 and 500 characters is required for complimentary Pro access.");
      return;
    }
    if (form.grantPro && !form.grantProAcknowledged) {
      setFormError("Acknowledge the complimentary access impact before creating this account.");
      return;
    }

    const { confirmPassword, grantProReason, grantProAcknowledged, ...payloadBase } = form;
    const payload = { ...payloadBase, ...(form.grantPro ? { reason: grantProReason.trim() } : {}) };
    createUserMutation.mutate(payload);
  };

  const handleDialogOpenChange = (open: boolean) => {
    if (!open) {
      setForm(emptyForm());
      setFormError(null);
    }
    setShowCreateDialog(open);
  };

  // ── Helpers ──────────────────────────────────────────────────────────────
  const formatDate = (dateString: string) =>
    new Date(dateString).toLocaleDateString("en-US", {
      year: "numeric",
      month: "long",
      day: "numeric",
    });

  const getUserDisplayName = (user: User) => {
    if (user.firstName || user.lastName) {
      return `${user.firstName ?? ""} ${user.lastName ?? ""}`.trim();
    }
    return user.email?.split("@")[0] ?? "Member";
  };

  // ── Render ───────────────────────────────────────────────────────────────
  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h2 className="text-2xl font-bold">User Database</h2>
          <p className="text-muted-foreground">Manage and view all user accounts</p>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          <Button onClick={() => setShowCreateDialog(true)} data-testid="btn-create-user">
            <UserPlus className="h-4 w-4 mr-2" />
            Create User
          </Button>
          <Button onClick={onClose} variant="outline">
            <ArrowLeft className="h-4 w-4 mr-2" />
            Back to Dashboard
          </Button>
        </div>
      </div>

      {/* Statistics Overview */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <Card>
          <CardContent className="p-4">
            <div className="text-2xl font-bold">
              {statsLoading ? <Skeleton className="h-8 w-16" /> : userStats?.totalUsers ?? 0}
            </div>
            <p className="text-sm text-muted-foreground">Total Users</p>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-4">
            <div className="text-2xl font-bold">
              {statsLoading ? <Skeleton className="h-8 w-16" /> : userStats?.newUsersThisMonth ?? 0}
            </div>
            <p className="text-sm text-muted-foreground">New This Month</p>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-4">
            <div className="text-2xl font-bold">
              {statsLoading ? <Skeleton className="h-8 w-16" /> : userStats?.activeUsers ?? 0}
            </div>
            <p className="text-sm text-muted-foreground">Active Users</p>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-4">
            <div className="text-2xl font-bold">
              {statsLoading ? (
                <Skeleton className="h-8 w-16" />
              ) : (
                userStats?.topLocations?.[0]?.favoriteCount ?? 0
              )}
            </div>
            <p className="text-sm text-muted-foreground">Top Spot Favorites</p>
          </CardContent>
        </Card>
      </div>

      {/* Search and source-aware filters */}
      <Card>
        <CardContent className="grid gap-3 p-4 md:grid-cols-[minmax(240px,1fr)_180px_220px]">
          <div className="relative">
            <Search className="absolute left-3 top-3 h-4 w-4 text-muted-foreground" />
            <Input maxLength={200} placeholder="Search name, email, or ctm_/sub_/txn_ Paddle ID…" value={searchTerm} onChange={(e) => changeListState({ search: e.target.value, offset: 0 }, true)} className="pl-10" data-testid="input-user-search" />
          </div>
          {isPaddleReference(searchTerm) && <label className="space-y-1 text-xs font-medium text-muted-foreground">Paddle environment <span className="text-destructive">*</span>
            <select required value={environment} onChange={event => changeListState({ environment: event.target.value as "" | "sandbox" | "live", offset: 0 })} className="h-10 w-full rounded-md border border-input bg-background px-3 text-sm text-foreground" aria-label="Paddle environment">
              <option value="">Choose environment</option><option value="sandbox">Sandbox</option><option value="live">Live</option>
            </select>
          </label>}
          <label className="space-y-1 text-xs font-medium text-muted-foreground">Effective tier
            <select value={tier} onChange={event => changeListState({ tier: event.target.value as typeof tier, offset: 0 })} className="h-10 w-full rounded-md border border-input bg-background px-3 text-sm text-foreground">
              <option value="all">All tiers</option><option value="free">Free</option><option value="pro">Pro</option><option value="agent">Agent</option>
            </select>
          </label>
          <label className="space-y-1 text-xs font-medium text-muted-foreground">Access source
            <select value={source} onChange={event => changeListState({ source: event.target.value as UserSourceFilter, offset: 0 })} className="h-10 w-full rounded-md border border-input bg-background px-3 text-sm text-foreground">
              <option value="all">All sources</option>
              <option value="paidAgent">Paid Agent</option><option value="paidPro">Paid Pro</option>
              <option value="complimentaryAgent">Complimentary Agent</option><option value="complimentaryPro">Complimentary Pro</option><option value="test">Test</option>
            </select>
          </label>
        </CardContent>
      </Card>

      {/* Users List */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center justify-between gap-3">
            <span>Members</span><span className="text-xs font-normal text-muted-foreground">{usersPage ? `${usersPage.total.toLocaleString()} matching` : "Loading member count"}</span>
          </CardTitle>
        </CardHeader>
        <CardContent>
          {isPaddleReference(searchTerm) && !environment && <p className="mb-4 rounded-lg border border-amber-700/20 bg-amber-50/70 p-3 text-sm text-amber-950" role="status">Choose Sandbox or Live to run an exact Paddle lookup. No account is selected until the lookup returns a clear match.</p>}
          {usersPage?.lookup && isPaddleReference(searchTerm) && environment && <div className={`mb-4 rounded-lg border p-3 text-sm ${usersPage.lookup.status === "matched" ? "border-teal-800/20 bg-teal-50/60" : "border-amber-700/20 bg-amber-50/70"}`} role="status">
            <p className="font-medium">{usersPage.lookup.message}</p>
            {usersPage.lookup.historical && <p className="mt-1 text-xs text-muted-foreground">Historical reference only.</p>}
            {usersPage.lookup.status !== "matched" && <p className="mt-1 text-xs text-muted-foreground">No customer detail was guessed or opened.</p>}
          </div>}
          {usersLoading ? (
            <div className="space-y-4">
              {[...Array(5)].map((_, i) => (
                <div key={i} className="flex items-center space-x-4 p-4 border rounded">
                  <Skeleton className="h-10 w-10 rounded-full" />
                  <div className="space-y-2">
                    <Skeleton className="h-4 w-48" />
                    <Skeleton className="h-3 w-32" />
                  </div>
                </div>
              ))}
            </div>
          ) : usersIsError ? (
            <div className="rounded-lg border border-destructive/25 bg-destructive/5 p-5" role="alert">
              <p className="font-medium">Member list could not be loaded</p>
              <p className="mt-1 text-sm text-muted-foreground">{usersError?.message ?? "The request failed."}</p>
              <Button className="mt-3" variant="outline" size="sm" onClick={() => void refetchUsers()}>Retry</Button>
            </div>
          ) : usersPage && usersPage.users.length > 0 ? (
            <div className="space-y-2">
              {usersPage.users.map((user) => (
                <div
                  key={user.id}
                  className={`flex flex-col gap-3 rounded-xl border p-3 transition-colors sm:flex-row sm:items-center sm:justify-between sm:p-4 ${!isPaddleReference(searchTerm) || usersPage.lookup?.status === "matched" ? "cursor-pointer hover:border-teal-900/20 hover:bg-teal-50/30" : "cursor-not-allowed opacity-70"}`}
                  role="link"
                  aria-disabled={isPaddleReference(searchTerm) && usersPage.lookup?.status !== "matched"}
                  tabIndex={isPaddleReference(searchTerm) && usersPage.lookup?.status !== "matched" ? -1 : 0}
                  onClick={() => { if (!isPaddleReference(searchTerm) || usersPage.lookup?.status === "matched") navigate(userDetailHref(user.id, listState)); }}
                  onKeyDown={event => { if ((event.key === "Enter" || event.key === " ") && (!isPaddleReference(searchTerm) || usersPage.lookup?.status === "matched")) { event.preventDefault(); navigate(userDetailHref(user.id, listState)); } }}
                  data-testid={`user-row-${user.id}`}
                >
                  <div className="flex min-w-0 items-center space-x-4">
                    {user.profileImageUrl ? (
                      <img
                        src={user.profileImageUrl}
                        alt="Profile"
                        className="w-10 h-10 rounded-full object-cover"
                      />
                    ) : (
                      <div className="flex h-10 w-10 items-center justify-center rounded-full bg-teal-100 text-teal-800 dark:bg-teal-900/40 dark:text-teal-100">
                        <User className="h-5 w-5" />
                      </div>
                    )}
                    <div className="min-w-0">
                      <p className="font-medium flex flex-wrap items-center gap-2 break-all">
                        {getUserDisplayName(user)}
                        {user.isSuspended && (
                          <Badge variant="destructive" data-testid={`badge-suspended-${user.id}`}>
                            Suspended
                          </Badge>
                        )}
                      </p>
                      <p className="text-sm text-muted-foreground break-all">{user.email}</p>
                    </div>
                  </div>
                  <div className="flex flex-wrap items-center justify-between gap-3 sm:justify-end">
                    <div className="flex max-w-sm flex-wrap gap-1">
                      <Badge className="capitalize bg-teal-700 hover:bg-teal-700">{user.effectiveTier}</Badge>
                      {Object.entries(user.accessSources).filter(([, enabled]) => enabled).map(([key]) => (
                        <Badge key={key} variant="outline" className="text-[10px]">{sourceName(key as keyof AccessSources)}</Badge>
                      ))}
                      {!Object.values(user.accessSources).some(Boolean) && <span className="text-xs text-muted-foreground">No paid/comp/test source</span>}
                    </div>
                    <div className="flex items-center gap-3">
                      <div className="hidden text-right text-xs text-muted-foreground lg:block">
                        <p>Joined {formatDate(user.createdAt)}</p><p>Active {formatDate(user.updatedAt)}</p>
                      </div>
                      <ChevronRight className="h-4 w-4 text-muted-foreground" />
                    </div>
                  </div>
                </div>
              ))}
              <div className="flex flex-col items-center justify-between gap-3 border-t pt-4 sm:flex-row">
                <p className="text-xs text-muted-foreground">
                  Showing {usersPage.offset + 1}–{Math.min(usersPage.offset + usersPage.users.length, usersPage.total)} of {usersPage.total.toLocaleString()}
                </p>
                <div className="flex gap-2">
                  <Button variant="outline" size="sm" disabled={usersPage.offset <= 0 || usersLoading} onClick={() => changeListState({ offset: Math.max(0, offset - usersPage.limit) })}>Previous</Button>
                  <Button variant="outline" size="sm" disabled={usersPage.offset + usersPage.users.length >= usersPage.total || usersLoading} onClick={() => changeListState({ offset: offset + usersPage.limit })}>Next</Button>
                </div>
              </div>
            </div>
          ) : (
            <div className="rounded-xl border border-dashed bg-teal-50/30 px-5 py-8 text-center dark:bg-teal-950/10">
              <Waves className="mx-auto h-6 w-6 text-teal-700/70" />
              <p className="mt-2 font-medium">No members match this view</p>
              <p className="mt-1 text-sm text-muted-foreground">Adjust the search or clear a tier/source filter.</p>
              <Button variant="ghost" size="sm" className="mt-2" onClick={() => changeListState({ search: "", tier: "all", source: "all", offset: 0, environment: "" })}>Clear filters</Button>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Top Locations */}
      {userStats?.topLocations && userStats.topLocations.length > 0 && (
        <Card>
          <CardHeader>
            <CardTitle>Most Popular Surf Spots</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-2">
              {userStats.topLocations.map((location, index) => (
                <div key={location.name} className="flex items-center justify-between p-2">
                  <div className="flex items-center space-x-2">
                    <Badge variant="secondary">{index + 1}</Badge>
                    <span className="font-medium">{location.name}</span>
                  </div>
                  <span className="text-sm text-muted-foreground">
                    {location.favoriteCount} favorites
                  </span>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      )}

      {/* ── Create User Dialog ─────────────────────────────────────────── */}
      <Dialog open={showCreateDialog} onOpenChange={handleDialogOpenChange}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>Create New Account</DialogTitle>
          </DialogHeader>

          <form onSubmit={handleCreateSubmit} className="space-y-4 mt-2">
            {/* Name row */}
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-1.5">
                <Label htmlFor="cu-first">First Name</Label>
                <Input
                  id="cu-first"
                  placeholder="Jane"
                  value={form.firstName}
                  onChange={(e) => setForm((f) => ({ ...f, firstName: e.target.value }))}
                  disabled={createUserMutation.isPending}
                />
              </div>
              <div className="space-y-1.5">
                <Label htmlFor="cu-last">Last Name</Label>
                <Input
                  id="cu-last"
                  placeholder="Doe"
                  value={form.lastName}
                  onChange={(e) => setForm((f) => ({ ...f, lastName: e.target.value }))}
                  disabled={createUserMutation.isPending}
                />
              </div>
            </div>

            {/* Email */}
            <div className="space-y-1.5">
              <Label htmlFor="cu-email">
                Email Address <span className="text-destructive">*</span>
              </Label>
              <Input
                id="cu-email"
                type="email"
                placeholder="jane@example.com"
                value={form.email}
                onChange={(e) => setForm((f) => ({ ...f, email: e.target.value }))}
                disabled={createUserMutation.isPending}
                required
                autoComplete="off"
              />
            </div>

            {/* Phone number */}
            <div className="space-y-1.5">
              <Label htmlFor="cu-phone">
                Phone Number <span className="text-destructive">*</span>
              </Label>
              <Input
                id="cu-phone"
                type="tel"
                placeholder="+14155552671"
                value={form.phoneNumber}
                onChange={(e) => setForm((f) => ({ ...f, phoneNumber: e.target.value }))}
                disabled={createUserMutation.isPending}
                required
                autoComplete="tel"
              />
              <p className="text-xs text-muted-foreground">
                Required by the sign-in provider. Use the user&apos;s number in international format.
              </p>
            </div>

            {/* Password */}
            <div className="space-y-1.5">
              <Label htmlFor="cu-password">
                Password <span className="text-destructive">*</span>
              </Label>
              <Input
                id="cu-password"
                type="password"
                placeholder="Min. 8 characters"
                value={form.password}
                onChange={(e) => setForm((f) => ({ ...f, password: e.target.value }))}
                disabled={createUserMutation.isPending}
                required
                autoComplete="new-password"
              />
            </div>

            {/* Confirm Password */}
            <div className="space-y-1.5">
              <Label htmlFor="cu-confirm">
                Confirm Password <span className="text-destructive">*</span>
              </Label>
              <Input
                id="cu-confirm"
                type="password"
                placeholder="Repeat password"
                value={form.confirmPassword}
                onChange={(e) => setForm((f) => ({ ...f, confirmPassword: e.target.value }))}
                disabled={createUserMutation.isPending}
                required
                autoComplete="new-password"
              />
            </div>

            {/* Grant Pro */}
            <div className="flex items-center gap-2 pt-1">
              <Checkbox
                id="cu-pro"
                checked={form.grantPro}
                onCheckedChange={(checked) =>
                  setForm((f) => ({ ...f, grantPro: checked === true }))
                }
                disabled={createUserMutation.isPending}
              />
              <Label htmlFor="cu-pro" className="cursor-pointer font-normal">
                Grant complimentary Pro access
              </Label>
            </div>
            {form.grantPro && <div className="space-y-3 rounded-lg border border-amber-700/20 bg-amber-50/60 p-3 dark:bg-amber-950/15">
              <p className="text-xs text-amber-950 dark:text-amber-100">This creates complimentary Pro access only. Other access sources are preserved; this does not change billing, cancel a subscription, or charge the customer.</p>
              <div className="space-y-1.5">
                <Label htmlFor="cu-pro-reason">Reason <span className="text-destructive">*</span></Label>
                <Input id="cu-pro-reason" value={form.grantProReason} onChange={e => setForm(f => ({ ...f, grantProReason: e.target.value.slice(0, 500) }))} disabled={createUserMutation.isPending} maxLength={500} placeholder="Why is complimentary access being granted?" />
                <p className="text-xs text-muted-foreground">{form.grantProReason.trim().length}/500 characters</p>
              </div>
              <div className="flex items-start gap-2">
                <Checkbox id="cu-pro-impact" checked={form.grantProAcknowledged} onCheckedChange={checked => setForm(f => ({ ...f, grantProAcknowledged: checked === true }))} disabled={createUserMutation.isPending} />
                <Label htmlFor="cu-pro-impact" className="cursor-pointer text-xs font-normal leading-relaxed">I confirm this is complimentary access only; it does not modify billing or any other access source.</Label>
              </div>
            </div>}

            {/* Welcome email notice */}
              <p className="text-xs text-muted-foreground pt-1">
              A welcome email with sign-in instructions will be sent to the address above.
            </p>

            {/* Error */}
            {formError && (
              <p className="text-sm text-destructive" role="alert">
                {formError}
              </p>
            )}

            <DialogFooter className="pt-2">
              <Button
                type="button"
                variant="outline"
                onClick={() => handleDialogOpenChange(false)}
                disabled={createUserMutation.isPending}
              >
                Cancel
              </Button>
              <Button type="submit" disabled={createUserMutation.isPending}>
                {createUserMutation.isPending ? (
                  <>
                    <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                    Creating…
                  </>
                ) : (
                  "Create Account"
                )}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
}
